import { keyBy } from 'lodash';
import {
  format,
  sub,
  add,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfYear,
} from 'date-fns';
import { ValueOf } from 'type-fest';

export const INTERVAL = {
  DAY: 'day',
  WEEK: 'week',
  MONTH: 'month',
  YEAR: 'year',
};

export const INTERVAL_PLURAL = {
  DAYS: 'days',
  WEEKS: 'weeks',
  MONTHS: 'months',
  YEARS: 'years',
};

export const INTERVAL_SET = new Set<string>(['day', 'week', 'month']);

export const INTERVAL_DISPLAY = {
  [INTERVAL.DAY]: 'Day',
  [INTERVAL.WEEK]: 'Week',
  [INTERVAL.MONTH]: 'Month',
  [INTERVAL.YEAR]: 'Year',
};

export const intervals: Array<IntervalType> = [
  {
    id: INTERVAL.DAY,
    label: 'Since 1 day ago',
    value: 1,
  },
  {
    id: INTERVAL.WEEK,
    label: 'Since 7 days ago',
    value: 7,
  },
  {
    id: INTERVAL.MONTH,
    label: 'Since 30 days ago',
    value: 30,
  },
  {
    id: INTERVAL.YEAR,
    label: 'Since 1 year ago',
    value: 365,
  },
];

export type IntervalType = {
  id: string;
  label: string;
  value: number;
};

export const intervalsHash = keyBy(intervals, 'id');

export const DATE_RANGE_OPTIONS = {
  LAST_12_WEEKS: 'LAST_12_WEEKS',
  LAST_12_MONTHS: 'LAST_12_MONTHS',
  YTD: 'YTD',
  CUSTOM: 'custom',
} as const;

// used for date range
export const dateRangeDefaultDateFormat = 'MM/dd/yyyy';

export const standardDateFormat = 'yyyy-MM-dd';

export const DATE_RANGE_OPTIONS_ARRAY = [
  {
    label: 'Last 12 weeks',
    value: DATE_RANGE_OPTIONS.LAST_12_WEEKS,
    generateDateRange: (dateFormat: string = dateRangeDefaultDateFormat) => {
      const now = new Date();
      return {
        start_date: format(startOfWeek(sub(now, { weeks: 12 })), dateFormat),
        end_date: format(endOfWeek(now), dateFormat),
      };
    },
  },
  {
    label: 'Last 12 months',
    value: DATE_RANGE_OPTIONS.LAST_12_MONTHS,
    generateDateRange: (dateFormat: string = dateRangeDefaultDateFormat) => {
      const now = new Date();
      return {
        start_date: format(startOfMonth(sub(now, { months: 12 })), dateFormat),
        end_date: format(endOfMonth(now), dateFormat),
      };
    },
  },
  {
    label: 'Years to Date',
    value: DATE_RANGE_OPTIONS.YTD,
    generateDateRange: (dateFormat: string = dateRangeDefaultDateFormat) => {
      const now = new Date();
      return {
        start_date: format(startOfYear(now), dateFormat),
        end_date: format(now, dateFormat),
      };
    },
  },
  {
    label: 'Custom',
    value: DATE_RANGE_OPTIONS.CUSTOM,
  },
];

export const DATE_RANGE_OPTIONS_VALUE_ARRAY = DATE_RANGE_OPTIONS_ARRAY.map((i) => i.value);

export const DATE_RANGE_OPTIONS_HASH = keyBy(DATE_RANGE_OPTIONS_ARRAY, 'value');

export type DateRangeOptionsValuesType = ValueOf<typeof DATE_RANGE_OPTIONS>;

///////

export const generateDateRange = ({
  customStartDate,
  customEndDate,
  option,
  format = dateRangeDefaultDateFormat,
}: {
  option: ValueOf<typeof DATE_RANGE_OPTIONS>;
  customStartDate?: string | null;
  customEndDate?: string | null;
  format?: string;
}) => {
  const dateRangeItem = DATE_RANGE_OPTIONS_HASH[option];

  if (!option || !dateRangeItem) return { start_date: undefined, end_date: undefined };

  const dateRange = dateRangeItem.generateDateRange?.(format);

  if (dateRange) return dateRange;

  return {
    start_date: customStartDate,
    end_date: customEndDate,
  };
};

// Use in DateRangeOptionSelectorUI

export enum DateRangeOptions {
  SevenDaysAgo = 'SevenDaysAgo',
  ThirtyDaysAgo = 'ThirtyDaysAgo',
  ThreeMonthsAgo = 'ThreeMonthsAgo',
  SixMonthsAgo = 'SixMonthsAgo',
  Custom = 'Custom',
}

export enum StartEndDates {
  StartDate = 'startDate',
  EndDate = 'endDate',
}

export const getTodayMoment = () => new Date();

const getFormattedToday = (dateFormat: string = standardDateFormat) =>
  format(getTodayMoment(), dateFormat);

export const getFormattedNextThirtyDaysRange = (dateFormat: string = standardDateFormat) => {
  return {
    startDate: getFormattedToday(dateFormat),
    endDate: format(add(getTodayMoment(), { months: 1 }), dateFormat),
  };
};

export const dateRangeOptionsArray = [
  {
    label: 'Last 7 days',
    value: DateRangeOptions.SevenDaysAgo,
    formatDates: () => ({
      startDate: format(sub(getTodayMoment(), { days: 7 }), standardDateFormat),
      endDate: getFormattedToday(),
    }),
  },
  {
    label: 'Last 30 days',
    value: DateRangeOptions.ThirtyDaysAgo,
    formatDates: () => ({
      startDate: format(sub(getTodayMoment(), { days: 30 }), standardDateFormat),
      endDate: getFormattedToday(),
    }),
  },
  {
    label: 'Last 90 days',
    value: DateRangeOptions.ThreeMonthsAgo,
    formatDates: () => ({
      startDate: format(sub(getTodayMoment(), { days: 90 }), standardDateFormat),
      endDate: getFormattedToday(),
    }),
  },
  {
    label: 'Last 180 days',
    value: DateRangeOptions.SixMonthsAgo,
    formatDates: () => ({
      startDate: format(sub(getTodayMoment(), { days: 180 }), standardDateFormat),
      endDate: getFormattedToday(),
    }),
  },
  {
    label: 'Custom',
    value: DateRangeOptions.Custom,
  },
];

export const FIVE_MINUTES = 5 * 60 * 1000;

export const dateRangeOptionsHash = keyBy(dateRangeOptionsArray, 'value');
